import { useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input } from "../../shared/ui";

/** Adds a public holiday to a calendar (§38). */
export function HolidayDrawer({
  calendarId,
  open,
  onClose,
  onSaved,
}: {
  calendarId: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const close = () => {
    setName("");
    setError(null);
    onClose();
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/v1/holidays", { calendarId, date, name });
      onSaved();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.code === "DUPLICATE_RESOURCE"
          ? "That calendar already has a holiday on that date."
          : "Could not add the holiday.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Add a holiday"
      subtitle="Non-working days reduce everyone's expected hours on this calendar."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={name.trim().length === 0}
          >
            {saving ? "Adding…" : "Add holiday"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Date" required>
          <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>

        <Field
          label="Name"
          required
          hint="A holiday falling on a non-working day subtracts nothing from capacity."
        >
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Independence Day"
            autoFocus
          />
        </Field>
      </div>
    </Drawer>
  );
}
